import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPacientes, createPaciente, buscarPacientePorCedulaSRI } from '../../api/pacienteService';
import { createHistoriaClinica } from '../../api/historiaClinicaService';
import GlobalDataTable from '../common/GlobalDataTable';
import GlobalSearchInput from '../common/GlobalSearchInput';
import { matchesSearchTerm } from '../../utils/globalSearch';
import { AuthContext } from '../../context/AuthContext';
import './PacientesList.css';

const PAGE_SIZE = 10;
const FETCH_BATCH_SIZE = 100;

const PacientesList = () => {
  const [busqueda, setBusqueda] = useState('');
  const [pacientes, setPacientes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formFeedback, setFormFeedback] = useState(null);
  const [consultandoSRI, setConsultandoSRI] = useState(false);
  const [cedulaCreandoHistoria, setCedulaCreandoHistoria] = useState(null);
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  // Estado para el formulario de nuevo paciente
  const [nuevoPaciente, setNuevoPaciente] = useState({
    nombre: '',
    apellido: '',
    cedula: '',
    telefono: '',
    email: '',
  });

  const extraerPacientes = (response) => {
    if (!response?.data) return [];
    if (response.data.pacientes) return response.data.pacientes;
    if (response.data.data?.pacientes) return response.data.data.pacientes;
    if (Array.isArray(response.data)) return response.data;
    return [];
  };

  const formatNumeroHistoriaClinica = (numero) => {
    if (typeof numero !== 'number') return '-';
    return `HC-${String(numero).padStart(4, '0')}`;
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';

    const fechaObj = new Date(fecha);
    if (Number.isNaN(fechaObj.getTime())) return 'Sin fecha';

    return fechaObj.toLocaleDateString('es-EC', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Manejar cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNuevoPaciente({
      ...nuevoPaciente,
      [name]: value
    });
  };

  const cargarPacientes = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await getPacientes({
        doctorId: currentUser?.doctor?.id,
        page: 1,
        limit: FETCH_BATCH_SIZE,
      });

      const pacientesPagina1 = extraerPacientes(response);
      const meta = response?.data?.data?.meta || response?.data?.meta || null;
      const totalPaginas = Math.max(1, meta?.pages || 1);

      const todasLasPaginas = [pacientesPagina1];

      for (let page = 2; page <= totalPaginas; page += 1) {
        const responsePage = await getPacientes({
          doctorId: currentUser?.doctor?.id,
          page,
          limit: FETCH_BATCH_SIZE,
        });

        todasLasPaginas.push(extraerPacientes(responsePage));
      }

      const pacientesData = todasLasPaginas.flat();

      setPacientes(pacientesData);

      setError(null);
    } catch (err) {
      console.error('Error al cargar pacientes:', err);
      setError('No se pudieron cargar los pacientes. Por favor, intente de nuevo más tarde.');

      setPacientes([
        {
          id: '1',
          numero: 1,
          nombre: 'Juan',
          apellido: 'Pérez',
          cedula: '1234567890',
          email: 'juan@example.com',
          telefono: '555-1234'
        },
        {
          id: '2',
          numero: 2,
          nombre: 'María',
          apellido: 'Gómez',
          cedula: '0987654321',
          email: 'maria@example.com',
          telefono: '555-5678'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    cargarPacientes();
  }, [cargarPacientes]);

  useEffect(() => {
    const handleDashboardAction = (event) => {
      if (event?.detail?.action === 'nuevo-paciente') {
        setShowForm(true);
      }
    };

    window.addEventListener('dashboard-action', handleDashboardAction);
    return () => window.removeEventListener('dashboard-action', handleDashboardAction);
  }, []);

  const pacientesFiltrados = pacientes.filter((paciente) => {
    return matchesSearchTerm(
      busqueda,
      `${paciente.nombre || ''} ${paciente.apellido || ''}`,
      paciente.cedula,
      paciente.email,
      paciente.telefono
    );
  });

  const obtenerHistoriaClinicaReciente = (paciente) => {
    if (Array.isArray(paciente?.historiaClinicas) && paciente.historiaClinicas.length > 0) {
      return paciente.historiaClinicas[0];
    }

    return null;
  };

  const tienePacienteHistoria = (paciente) => {
    if (Array.isArray(paciente?.historiaClinicas) && paciente.historiaClinicas.length > 0) {
      return true;
    }

    return (paciente?._count?.historiaClinicas || 0) > 0;
  };

  // Función para crear nueva historia clínica
  const crearNuevaHistoriaClinica = async (cedula) => {
    try {
      setCedulaCreandoHistoria(cedula);
      await createHistoriaClinica({ cedula });
      await cargarPacientes();
      navigate(`/dashboard/historia-clinica/${cedula}`);
    } catch (error) {
      console.error('Error al crear historia clínica:', error);
      alert(error?.response?.data?.message || 'No se pudo crear la historia clínica automáticamente.');
    } finally {
      setCedulaCreandoHistoria(null);
    }
  };

  const columnasPacientes = [
    {
      key: 'numeroHistoria',
      header: 'N° Historia Clínica',
      render: (paciente) => formatNumeroHistoriaClinica(paciente.numero),
    },
    {
      key: 'nombre',
      header: 'Nombre',
      render: (paciente) => `${paciente.nombre} ${paciente.apellido}`,
    },
    {
      key: 'cedula',
      header: 'Cédula',
      render: (paciente) => paciente.cedula,
    },
    {
      key: 'email',
      header: 'Email',
      render: (paciente) => (
        paciente.email
          ? paciente.email
          : <span className="global-table-muted">No registrado</span>
      ),
    },
    {
      key: 'telefono',
      header: 'Teléfono',
      render: (paciente) => (
        paciente.telefono
          ? paciente.telefono
          : <span className="global-table-muted">No registrado</span>
      ),
    },
    {
      key: 'ultimaHistoria',
      header: 'Última Historia Clínica',
      render: (paciente) => {
        const historiaReciente = obtenerHistoriaClinicaReciente(paciente);

        return historiaReciente ? (
          <div>
            <div style={{ fontWeight: 600 }}>
              HC creada: {formatearFecha(historiaReciente.createdAt)}
            </div>
            <div className="global-table-subtext">
              Última actualización: {formatearFecha(historiaReciente.updatedAt)}
            </div>
          </div>
        ) : (
          <span className="global-table-muted">Sin historia registrada</span>
        );
      },
    },
    {
      key: 'acciones',
      header: 'Acciones',
      align: 'center',
      render: (paciente) => {
        const tieneHistoria = tienePacienteHistoria(paciente);

        return (
          <div className="global-table-actions">
            {tieneHistoria ? (
              <Link to={`/dashboard/historia-clinica/${paciente.cedula}`} className="global-table-btn global-table-btn--primary">
                <i className="fas fa-file-medical"></i>
                Abrir Historia
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => crearNuevaHistoriaClinica(paciente.cedula)}
                disabled={cedulaCreandoHistoria === paciente.cedula}
                className="global-table-btn global-table-btn--warning"
              >
                <i className="fas fa-plus-circle"></i>
                {cedulaCreandoHistoria === paciente.cedula ? 'Creando...' : 'Crear Historia'}
              </button>
            )}

            <Link to={`/dashboard/citas?paciente=${paciente.cedula}`} className="global-table-btn global-table-btn--info">
              <i className="fas fa-calendar-alt"></i>
              Ver Citas
            </Link>
          </div>
        );
      },
    },
  ];

  const handleBuscarPacienteSRI = async () => {
    const cedula = (nuevoPaciente.cedula || '').trim();

    if (!/^\d{10}$/.test(cedula)) {
      setFormFeedback({
        tipo: 'error',
        mensaje: 'Ingrese una cédula válida de 10 dígitos para consultar en SRI'
      });
      return;
    }

    try {
      setConsultandoSRI(true);

      const response = await buscarPacientePorCedulaSRI(cedula);
      const sriData = response?.data?.data || response?.data;

      if (!sriData?.nombres && !sriData?.apellidos && !sriData?.nombreCompleto) {
        setFormFeedback({
          tipo: 'error',
          mensaje: 'SRI no devolvió datos de nombres para esta cédula'
        });
        return;
      }

      setNuevoPaciente((prev) => ({
        ...prev,
        cedula,
        nombre: sriData.nombres || prev.nombre,
        apellido: sriData.apellidos || prev.apellido,
      }));

      setFormFeedback({
        tipo: 'exito',
        mensaje: `Datos encontrados en SRI para ${sriData.nombreCompleto || cedula}`
      });
    } catch (error) {
      const mensajeError =
        error?.response?.data?.message
        || 'No se pudo consultar SRI en este momento';

      setFormFeedback({
        tipo: 'error',
        mensaje: mensajeError
      });
    } finally {
      setConsultandoSRI(false);
    }
  };

  // Función para crear nuevo paciente
  const handleCrearPaciente = async (e) => {
    e.preventDefault();
    
    // Validar que todos los campos obligatorios estén completos
    if (!nuevoPaciente.nombre || !nuevoPaciente.apellido || !nuevoPaciente.cedula) {
      setFormFeedback({
        tipo: 'error',
        mensaje: 'Por favor complete todos los campos obligatorios (nombre, apellido y cédula)'
      });
      return;
    }
    
    // Validar formato de cédula (10 dígitos numéricos)
    if (!/^\d{10}$/.test(nuevoPaciente.cedula)) {
      setFormFeedback({
        tipo: 'error',
        mensaje: 'La cédula debe contener exactamente 10 dígitos numéricos'
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Datos para enviar al backend
      const pacienteData = {
        ...nuevoPaciente,
        doctorId: currentUser?.doctor?.id
      };
      
      const response = await createPaciente(pacienteData);
      const pacienteCreado =
        response?.data?.data?.paciente ||
        response?.data?.paciente ||
        response?.data?.data ||
        response?.data;
      
      if (pacienteCreado) {
        setFormFeedback({
          tipo: 'exito',
          mensaje: 'Paciente creado correctamente. La historia clínica inicial se generó automáticamente.'
        });
        
        setNuevoPaciente({
          nombre: '',
          apellido: '',
          cedula: '',
          telefono: '',
          email: '',
        });
        
        setShowForm(false);

        await cargarPacientes();
      }
    } catch (error) {
      console.error('Error al crear paciente:', error);
      
      let mensajeError = 'Error al crear el paciente';
      
      if (error.response && error.response.data && error.response.data.message) {
        mensajeError = error.response.data.message;
      }
      
      setFormFeedback({
        tipo: 'error',
        mensaje: mensajeError
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="patients-page">
      
      {/* Formulario para nuevo paciente */}
      {showForm && (
        <div className="patients-form-card">
          <h3 className="patients-form-title">
            <i className="fas fa-user-plus" style={{ color: '#6a5acd' }}></i>
            Registrar Nuevo Paciente
          </h3>
          
          {formFeedback && (
            <div className={`patients-feedback ${formFeedback.tipo === 'error' ? 'error' : 'success'}`}>
              <i className={`fas fa-${formFeedback.tipo === 'error' ? 'exclamation-circle' : 'info-circle'}`}></i>
              {formFeedback.mensaje}
            </div>
          )}
          
          <form onSubmit={handleCrearPaciente}>
            <div className="patients-form-grid">
              <div>
                <label className="patients-label">
                  Cédula <span className="patients-required">*</span>
                </label>
                <div className="patients-input-wrap">
                  <i className="fas fa-id-card patients-input-icon"></i>
                  <input 
                    type="text" 
                    name="cedula"
                    value={nuevoPaciente.cedula}
                    onChange={handleInputChange}
                    className="patients-input has-icon"
                    placeholder="Ej: 1712345678"
                    pattern="[0-9]{10}"
                    title="Ingrese un número de cédula válido de 10 dígitos"
                    required
                  />
                </div>
                <div className="patients-hint">
                  Debe ser un número de 10 dígitos sin guiones ni espacios
                </div>
                <div className="patients-cedula-search-action">
                  <button
                    type="button"
                    onClick={handleBuscarPacienteSRI}
                    disabled={consultandoSRI}
                    className="patients-btn secondary"
                  >
                    <i className="fas fa-search"></i>
                    {consultandoSRI ? 'Consultando...' : 'Buscar'}
                  </button>
                </div>
              </div>

              <div>
                <label className="patients-label">
                  Nombre <span className="patients-required">*</span>
                </label>
                <div className="patients-input-wrap">
                  <i className="fas fa-user patients-input-icon"></i>
                  <input 
                    type="text" 
                    name="nombre"
                    value={nuevoPaciente.nombre}
                    onChange={handleInputChange}
                    className="patients-input has-icon"
                    placeholder="Ingrese nombre del paciente"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="patients-label">
                  Apellido <span className="patients-required">*</span>
                </label>
                <div className="patients-input-wrap">
                  <i className="fas fa-user patients-input-icon"></i>
                  <input 
                    type="text" 
                    name="apellido"
                    value={nuevoPaciente.apellido}
                    onChange={handleInputChange}
                    className="patients-input has-icon"
                    placeholder="Ingrese apellido del paciente"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="patients-label">
                  Teléfono
                </label>
                <div className="patients-input-wrap">
                  <i className="fas fa-phone patients-input-icon"></i>
                  <input 
                    type="tel" 
                    name="telefono"
                    value={nuevoPaciente.telefono}
                    onChange={handleInputChange}
                    className="patients-input has-icon"
                    placeholder="Ej: 0991234567"
                  />
                </div>
              </div>
              
              <div>
                <label className="patients-label">
                  Email
                </label>
                <div className="patients-input-wrap">
                  <i className="fas fa-envelope patients-input-icon"></i>
                  <input 
                    type="email" 
                    name="email"
                    value={nuevoPaciente.email}
                    onChange={handleInputChange}
                    className="patients-input has-icon"
                    placeholder="email@ejemplo.com"
                  />
                </div>
              </div>
            </div>
            
            <div className="patients-actions">
              <button 
                type="submit" 
                disabled={isLoading}
                className="patients-btn primary"
              >
                <i className="fas fa-save"></i>
                {isLoading ? 'Guardando...' : 'Guardar Paciente'}
              </button>
              
              <button 
                type="button" 
                onClick={() => setShowForm(false)}
                className="patients-btn secondary"
              >
                <i className="fas fa-times"></i>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Búsqueda */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ maxWidth: '500px' }}>
          <GlobalSearchInput
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, cédula o email"
          />
        </div>
      </div>
      
      {/* Estado de carga */}
      {isLoading && (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          border: '1px solid #eaeaea'
        }}>
          <div style={{ 
            width: '50px',
            height: '50px',
            position: 'relative',
            margin: '0 auto 15px'
          }}>
            <div style={{ 
              border: '3px solid rgba(106, 90, 205, 0.1)',
              borderTop: '3px solid #6a5acd',
              borderRadius: '50%',
              width: '100%',
              height: '100%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
          <p style={{ color: '#666', margin: 0 }}>Cargando pacientes...</p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}
      
      {/* Mensaje de error */}
      {error && (
        <div style={{ 
          padding: '25px', 
          backgroundColor: '#fff2f5',
          borderRadius: '10px',
          border: '1px solid #fdd1da',
          color: '#e53e3e',
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <i className="fas fa-exclamation-circle" style={{ fontSize: '24px' }}></i>
          <p style={{ margin: 0 }}>{error}</p>
        </div>
      )}
      
      {/* Tabla de pacientes */}
      {!isLoading && !error && (
        <GlobalDataTable
          columns={columnasPacientes}
          rows={pacientesFiltrados}
          rowKey="id"
          emptyMessage="No se encontraron pacientes"
          emptyIcon={<i className="fas fa-search"></i>}
          enablePagination
          rowsPerPage={PAGE_SIZE}
          pageResetKey={busqueda}
        />
      )}
    </div>
  );
};

export default PacientesList;